import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { FaTimes, FaUpload, FaImage } from 'react-icons/fa';
import { useMediaQuery } from '../../hooks/useMediaQuery';

const ImageUploadModal = ({ onClose, onUpload, documentType = "profile_photo", maxSize = 5 * 1024 * 1024 }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [uploadStep, setUploadStep] = useState('upload'); // 'upload', 'progress', 'edit'
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({
    unit: '%',
    width: 80,
    height: 80,
    x: 10,
    y: 10,
    aspect: 1
  });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [imageRef, setImageRef] = useState(null);
  const [uploadInterval, setUploadInterval] = useState(null);
  const [validationError, setValidationError] = useState(null);

  // Document type validation
  const validDocumentTypes = [
    'license', 'certification', 'resume', 'contract', 'profile_photo', 
    'degree', 'training_certificate', 'achievement', 'award', 'publication', 
    'report', 'presentation', 'media', 'reference_letter', 'other'
  ];

  // File size limits by document type (in MB)
  const fileSizeLimits = {
    'profile_photo': 5,
    'resume': 10,
    'license': 5,
    'certification': 5,
    'degree': 10,
    'training_certificate': 5,
    'achievement': 5,
    'award': 5,
    'publication': 10,
    'report': 20,
    'presentation': 50,
    'media': 100,
    'reference_letter': 5,
    'contract': 10,
    'other': 10
  };

  const validateFile = (file) => {
    setValidationError(null);
    
    // Check document type
    if (!validDocumentTypes.includes(documentType)) {
      setValidationError(`Invalid document type: ${documentType}`);
      return false;
    }

    // Check file size
    const maxSizeMB = fileSizeLimits[documentType] || 10;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    
    if (file.size > maxSizeBytes) {
      setValidationError(`File size exceeds ${maxSizeMB}MB limit for ${documentType}`);
      return false;
    }

    // Check file type for images
    if (documentType === 'profile_photo') {
      const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!validImageTypes.includes(file.type)) {
        setValidationError('Only JPEG and PNG files are allowed for profile photos');
        return false;
      }
    }

    return true;
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      // Validate file before processing
      if (!validateFile(file)) {
        return;
      }
      
      setSelectedFile(file);
      setUploadStep('progress');
      
      // Simulate upload progress
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 30;
        if (progress >= 100) {
          progress = 100;
          clearInterval(interval);
          setTimeout(() => {
            setUploadStep('edit');
            const reader = new FileReader();
            reader.onload = () => {
              setImageSrc(reader.result);
            };
            reader.readAsDataURL(file);
          }, 500);
        }
        setUploadProgress(progress);
      }, 200);
      setUploadInterval(interval);
    }
  }, [documentType]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    maxFiles: 1,
    maxSize: fileSizeLimits[documentType] * 1024 * 1024 || 5 * 1024 * 1024
  });

  const handleSave = () => {
    if (imageRef && crop.width && crop.height) {
      const canvas = document.createElement('canvas');
      const scaleX = imageRef.naturalWidth / imageRef.width;
      const scaleY = imageRef.naturalHeight / imageRef.height;
      
      // Use actual pixel dimensions for better quality
      const pixelCrop = {
        x: crop.x * scaleX,
        y: crop.y * scaleY,
        width: crop.width * scaleX,
        height: crop.height * scaleY,
      };
      
      // Set canvas to actual crop size for best quality
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext('2d');

      // Draw the cropped portion directly without additional transforms
      ctx.drawImage(
        imageRef,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        canvas.width,
        canvas.height
      );

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        onUpload(url);
      }, 'image/jpeg', 0.95);
    }
  };

  const handleCancelUpload = () => {
    if (uploadInterval) {
      clearInterval(uploadInterval);
      setUploadInterval(null);
    }
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadStep('upload');
  };

  const renderUploadStep = () => (
    <div className="text-center flex-1 h-full">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg ${isMobile ? 'p-4' : 'p-8'} cursor-pointer transition-colors h-full flex items-center justify-center ${
          isDragActive ? 'border-blue-400 bg-blue-50' : 'border-blue-300 bg-blue-50'
        }`}
      >
        <input {...getInputProps()} />
        <div className={`flex flex-col items-center ${isMobile ? 'gap-2' : 'gap-4'}`}>
          <div className={`${isMobile ? 'w-12 h-12' : 'w-16 h-16'} bg-blue-200 rounded-lg flex items-center justify-center`}>
            <FaImage className={`text-blue-600 ${isMobile ? 'text-xl' : 'text-2xl'}`} />
          </div>
          <div>
            <p className={`text-gray-700 ${isMobile ? 'text-sm' : ''}`}>
              Drag & drop image or <span className="text-blue-600 font-medium">Browse</span>
            </p>
            <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500 mt-1`}>
              Supported formats: PNG, JPEG Max 5 MB
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProgressStep = () => (
    <div className="flex-1 h-full flex flex-col">
      <div className="flex-1 flex items-center justify-center">
        <div
          {...getRootProps()}
          className={`border-2 flex-1 border-dashed rounded-lg ${isMobile ? 'p-4' : 'p-8'} cursor-pointer transition-colors border-blue-300 bg-blue-50 h-full flex items-center justify-center`}
        >
          <input {...getInputProps()} />
          <div className={`flex flex-col items-center ${isMobile ? 'gap-2' : 'gap-4'}`}>
            <div className={`${isMobile ? 'w-12 h-12' : 'w-16 h-16'} bg-blue-200 rounded-lg flex items-center justify-center`}>
              <FaImage className={`text-blue-600 ${isMobile ? 'text-xl' : 'text-2xl'}`} />
            </div>
            <div>
              <p className={`text-gray-700 ${isMobile ? 'text-sm' : ''}`}>
                Drag & drop image or <span className="text-blue-600 font-medium">Browse</span>
              </p>
              <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500 mt-1`}>
                Supported formats: PNG, JPEG Max 5 MB
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Section */}
      <div className={`space-y-4 ${isMobile ? 'mt-4' : 'mt-6'}`}>
        <div className="flex items-center justify-between">
          <p className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700`}>
            Uploading - 1/1 file
          </p>
          <button
            onClick={handleCancelUpload}
            className="text-red-500 hover:text-red-700"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600 truncate`}>{selectedFile?.name}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-green-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${uploadProgress}%` }}
          ></div>
        </div>
      </div>
    </div>
  );

  const renderEditStep = () => (
    <div className="flex-1 h-full flex flex-col overflow-hidden">
      <div className="flex-1 flex justify-center items-center overflow-hidden">
        <div className="relative">
          <ReactCrop
            crop={crop}
            onChange={(c) => setCrop(c)}
            aspect={1}
            circularCrop
          >
            <img
              ref={setImageRef}
              src={imageSrc}
              alt="Crop"
              style={{
                transform: `scale(${zoom}) rotate(${rotation}deg)`,
                maxWidth: isMobile ? '250px' : '400px',
                maxHeight: isMobile ? '180px' : '300px'
              }}
            />
          </ReactCrop>
        </div>
      </div>

      {/* Controls */}
      {isMobile ? (
        // Mobile: Stack controls vertically
        <div className="flex flex-col gap-3 mt-3">
          {/* Zoom Control */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-700 w-14 flex-shrink-0">Zoom:</span>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-xs text-gray-500">+</span>
              <input
                type="range"
                min="0.5"
                max="3"
                step="0.1"
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="flex-1 min-w-0"
              />
              <span className="text-xs text-gray-500 w-10 text-right flex-shrink-0">
                {Math.round(zoom * 100)}%
              </span>
            </div>
          </div>

          {/* Rotation Control */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-700 w-14 flex-shrink-0">Rotate:</span>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-xs text-gray-500">↻</span>
              <input
                type="range"
                min="-180"
                max="180"
                step="1"
                value={rotation}
                onChange={(e) => setRotation(parseInt(e.target.value))}
                className="flex-1 min-w-0"
              />
              <span className="text-xs text-gray-500 w-10 text-right flex-shrink-0">
                {rotation}°
              </span>
            </div>
          </div>
        </div>
      ) : (
        // Desktop: Keep original horizontal layout
        <div className="flex items-center gap-6 mt-6">
          {/* Zoom Control */}
          <div className="flex items-center gap-3 flex-1">
            <span className="text-sm font-medium text-gray-700">Zoom:</span>
            <div className="flex items-center gap-2 flex-1">
              <span className="text-sm text-gray-500">+</span>
              <input
                type="range"
                min="0.5"
                max="3"
                step="0.1"
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm text-gray-500 w-12 text-right">
                {Math.round(zoom * 100)}%
              </span>
            </div>
          </div>

          {/* Rotation Control */}
          <div className="flex items-center gap-3 flex-1">
            <span className="text-sm font-medium text-gray-700">Rotation:</span>
            <div className="flex items-center gap-2 flex-1">
              <span className="text-sm text-gray-500">↻</span>
              <input
                type="range"
                min="-180"
                max="180"
                step="1"
                value={rotation}
                onChange={(e) => setRotation(parseInt(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm text-gray-500 w-12 text-left">
                {rotation}°
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex justify-center items-center p-4">
      <div className={`bg-white ${isMobile ? 'rounded-2xl p-4 w-full max-w-[360px] h-[85vh]' : 'rounded-3xl p-6 w-[600px] h-[80vh]'} flex flex-col`}>
        {/* Header */}
        <div className={`flex justify-between items-center ${isMobile ? 'mb-3' : 'mb-6'}`}>
          <h2 className={`${isMobile ? 'text-base' : 'text-xl'} font-bold text-gray-900`}>
            {uploadStep === 'upload' && 'Upload new image'}
            {uploadStep === 'progress' && 'Uploading...'}
            {uploadStep === 'edit' && 'Edit image'}
          </h2>
          <button
            onClick={onClose}
            className={`text-gray-500 hover:text-gray-700 ${isMobile ? 'text-xl' : 'text-xl'} font-bold`}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0">
          {uploadStep === 'upload' && renderUploadStep()}
          {uploadStep === 'progress' && renderProgressStep()}
          {uploadStep === 'edit' && renderEditStep()}
        </div>

        {/* Error Display */}
        {validationError && (
          <div className={`${isMobile ? 'mt-2 p-2' : 'mt-4 p-3'} bg-red-50 border border-red-200 rounded-lg`}>
            <div className="flex items-center">
              <svg className="h-5 w-5 text-red-400 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-red-700`}>{validationError}</span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className={`flex ${isMobile ? 'flex-col' : 'justify-end'} gap-2 ${isMobile ? 'mt-3' : 'mt-6'}`}>
          <button
            onClick={onClose}
            className={`bg-white border border-gray-300 text-gray-700 ${isMobile ? 'w-full px-3 py-2 text-sm' : 'px-4 py-2'} rounded-md font-medium hover:bg-gray-50 transition-colors`}
          >
            Cancel
          </button>
          {uploadStep === 'edit' && (
            <button
              onClick={handleSave}
              className={`bg-blue-600 hover:bg-blue-700 text-white ${isMobile ? 'w-full px-3 py-2 text-sm' : 'px-4 py-2'} rounded-md font-medium transition-colors`}
            >
              Save
            </button>
          )}
          {uploadStep === 'upload' && (
            <button
              onClick={() => document.querySelector('input[type="file"]').click()}
              className={`bg-blue-600 hover:bg-blue-700 text-white ${isMobile ? 'w-full px-3 py-2 text-sm' : 'px-4 py-2'} rounded-md font-medium transition-colors`}
            >
              Upload
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageUploadModal; 